// ===========================================
// Student Dashboard Page
// ===========================================

import { Box, Skeleton, Alert } from '@mui/material';
import PageHeader from '../../components/common/PageHeader';
import { useAuth } from '../../contexts/useAuth';
import {
  useMyStudentProfile,
  useMyStudentEnrollments,
  useMyStudentNotes,
  useMyStudentResources,
  useUpdateStudentAvatar,
} from '../../hooks/useStudentProfile';
import { useViewTerms } from '../../hooks/useViewTerms';
import StudentDashboardContent from '../../components/student/StudentDashboardContent';

export default function StudentDashboardPage() {
  const { user } = useAuth();

  const { data: profile, isLoading: profileLoading } = useMyStudentProfile();
  const { data: enrollments = [], isLoading: enrollmentsLoading } = useMyStudentEnrollments();
  const { data: notes = [], isLoading: notesLoading } = useMyStudentNotes();
  const { data: resources = [], isLoading: resourcesLoading } = useMyStudentResources();
  const { data: terms = [] } = useViewTerms(); // a student cannot read /admin/terms (#272)
  const updateAvatar = useUpdateStudentAvatar();

  if (profileLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Skeleton variant="rectangular" width="100%" height={400} />
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box>
        <PageHeader
          title="Student Dashboard"
          subtitle={`Welcome${user?.firstName ? `, ${user.firstName}` : ''}!`}
        />
        <Alert severity="error">
          Student profile not found. Please contact your school administrator.
        </Alert>
      </Box>
    );
  }

  return (
    <StudentDashboardContent
      profile={profile}
      enrollments={enrollments}
      enrollmentsLoading={enrollmentsLoading}
      notes={notes}
      notesLoading={notesLoading}
      resources={resources}
      resourcesLoading={resourcesLoading}
      terms={terms}
      onAvatarChange={(avatar) => updateAvatar.mutate(avatar!)}
      avatarChangePending={updateAvatar.isPending}
      isParentView={false}
    />
  );
}
